import { type FC } from 'react'
import { Spinner } from '../../../../components/Spinner/Spinner'
import { type SmsTemplate } from '../../../../models/Scheduling'
import { useAppSelector } from '../../../../store/hooks'
import { selectSafeAuthenticatedUser } from '../../../../store/auth/authSlice'
import { useGetSchedulingSmsTemplatesQuery } from '../../../../store/scheduling/scheduling'
import { TemplateCard, TemplateGroup, TemplateText } from './messageTemplateCards'
import { SMS_TEMPLATE_PURPOSES } from './smsTemplatePurposes'

interface SmsTemplatePreviewProps {
  template?: SmsTemplate
  note: string
}

const SmsTemplatePreview: FC<SmsTemplatePreviewProps> = ({ template, note }) =>
  template != null ? (
    <div className='d-flex flex-column gap-3 p-3 border rounded-2 paragraph-small'>
      <div className='d-flex align-items-baseline gap-2'>
        <span className='lead-small zocks-text-gray-500 text-uppercase'>From</span>
        <span>Your sending number</span>
      </div>
      <div className='p-3 rounded-3 zocks-bg-color-gray-100'>
        <TemplateText text={template.body} />
      </div>
      <span className='zocks-text-gray-600'>{note}</span>
    </div>
  ) : (
    <div className='paragraph-small zocks-text-gray-600'>This template isn&apos;t available yet.</div>
  )

interface SmsTemplateGroupProps {
  expandedId: string | null
  onToggle: (id: string) => void
}

export const SmsTemplateGroup: FC<SmsTemplateGroupProps> = ({ expandedId, onToggle }) => {
  const accountId = useAppSelector(selectSafeAuthenticatedUser).accountId
  const { data: templates = [], isLoading, isError } = useGetSchedulingSmsTemplatesQuery({ accountId })

  return (
    <TemplateGroup title='Texts sent to clients'>
      {isLoading ? (
        <div className='d-flex justify-content-center py-3'>
          <Spinner />
        </div>
      ) : null}
      {isError ? <div className='paragraph-small zocks-text-gray-600'>Could not load the text templates. Please try again.</div> : null}
      {!isLoading && !isError
        ? SMS_TEMPLATE_PURPOSES.map((info) => {
            const id = `sms-template-${info.purpose}`
            return (
              <TemplateCard
                key={id}
                regionId={id}
                label={info.label}
                description={info.description}
                isExpanded={expandedId === id}
                onToggle={() => onToggle(id)}
              >
                <SmsTemplatePreview template={templates.find((template) => template.purpose === info.purpose)} note={info.note} />
              </TemplateCard>
            )
          })
        : null}
    </TemplateGroup>
  )
}
